import React, { useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { formatTime } from 'shared/index'
import { MspDispatchContext, MSP_ACTIONS } from 'shared/components/Multistep'

import sddService from 'selfServicePortal/services/CreditCardServices/sddService'
import {
	ISddEnrollmentCompleteResponse,
	OtpDistributionChannel,
} from 'selfServicePortal/interfaces/i-api-sdd'

import { SddDispatchContext, SDD_ACTIONS } from '../../SDDMultistepValues'
import { AuthContext } from '../SDDAuthentication'

export const maskedEmail = (email: string | undefined) => {
	if (email === undefined) {
		return undefined
	}
	let maskedEmailPart1
	let maskedEmailPart2
	let emailString = email
	let firstLetter = emailString.substring(0, 1)
	let middleAt = emailString.search(/@/)
	let lastDot = emailString.search(/\.(?:.(?!\.))+$/)

	maskedEmailPart1 = emailString.substring(1, middleAt - 1).replace(/./g, '*')
	maskedEmailPart2 = emailString
		.substring(middleAt + 1, lastDot)
		.replace(/./g, '*')

	return (
		firstLetter +
		maskedEmailPart1 +
		emailString.substring(middleAt - 1, middleAt + 2) +
		maskedEmailPart2 +
		emailString.substring(lastDot, emailString.length)
	)
}

export const maskedPhoneNr = (phoneNr: string | undefined) => {
	if (phoneNr === undefined) {
		return undefined
	}
	let maskedNr
	let phoneNumber = phoneNr
	let firstThree = phoneNumber.substring(0, 3)
	let lastThree = phoneNumber.substring(phoneNumber.length - 3)
	maskedNr = phoneNumber
		.substring(3, phoneNumber.length - 3)
		.replace(/\d/g, '*')
	return firstThree + maskedNr + lastThree
}

export const AuthValidation = () => {
	const { t } = useTranslation()
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [sdd, dispatchSdd] = useContext(SddDispatchContext)
	const {
		setAuthSelect,
		otpCode,
		setOtpCode,
		setLoading,
		setErr,
		otpSessionResponse,
	} = useContext(AuthContext)
	const [time, setTime] = useState<string>()
	const [timeOut, setTimeOut] = useState<boolean>(false)

	const CountdownTimer = useCallback(
		(time: string) => {
			function updateRemainingTime() {
				const difference = +new Date(time) - +new Date()
				let remaining
				if (difference > 0) {
					const parts: any = {
						days: Math.floor(difference / (1000 * 60 * 60 * 24)),
						hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
						minutes: Math.floor((difference / 1000 / 60) % 60),
						seconds: formatTime(
							Math.floor((difference / 1000) % 60),
						),
					}
					remaining = `${parts.minutes}:${parts.seconds} ${t(
						'Minutes',
					)}`
				} else {
					remaining = `${t('OtpTimeOut')}`
					setTimeOut(true)
				}
				return setTime(remaining)
			}
			return updateRemainingTime()
		},
		[t],
	)

	const setOtpData = useCallback(() => {
		dispatchSdd({
			type: SDD_ACTIONS.SET_OTP_INFO,
			payload: {
				otpDistributedToContactInfo:
					otpSessionResponse?.otpDistributedToContactInfo,
				sessionId: otpSessionResponse?.sessionId,
				expiryAtUtc: otpSessionResponse?.expiryAtUtc,
			},
		})
	}, [otpSessionResponse, dispatchSdd])

	useEffect(() => {
		setOtpData()
		if (otpSessionResponse?.expiryAtUtc !== undefined) {
			const interval = window.setInterval(() => {
				CountdownTimer(otpSessionResponse?.expiryAtUtc)
			}, 1000)

			return () => window.clearInterval(interval)
		}
		return
	}, [otpSessionResponse, setOtpData, CountdownTimer])

	useEffect(() => {
		dispatchSdd({
			type: SDD_ACTIONS.SET_DESCRIPTION,
			payload: {
				description: `${
					sdd.distributionChannel ===
					OtpDistributionChannel.PushNotification
						? 'SDDAuthAppSubtitle'
						: 'SDDAuthOtpSubtitle'
				}`,
			},
		})
	}, [dispatchSdd, sdd.distributionChannel])

	function setAuthComplete(response: ISddEnrollmentCompleteResponse) {
		dispatchSdd({
			type: SDD_ACTIONS.SET_MANDATE_DOC,
			payload: {
				mandateDocumentId: response.mandateDocumentId,
			},
		})
	}

	async function validateOtp(otpCode: string) {
		if (otpCode !== undefined) {
			try {
				sddService
					.sddEnrollmentComplete({
						sessionId: sdd.sessionId,
						customerId: sdd.customerId,
						otpCode: otpCode,
						debtorIban: sdd.debtorIban,
						debtorBic: sdd.debtorBic,
						sddWithdrawalType: sdd.sddWithdrawalType,
						withdrawalAmount: sdd.withdrawalAmount,
					})
					.then((response) => {
						if (response.status === 200) {
							console.log(response.data)
							setAuthComplete(response.data)
							dispatchMsp({
								type: MSP_ACTIONS.SET_INDEX,
								payload: {
									...msp,
									index: msp.index + 1,
								},
							})
							window.scrollTo({
								top: 0,
								left: 0,
								behavior: 'smooth',
							})
							setLoading(false)
						}
					})
					.catch((error) => {
						console.log(error)
						setErr(true)
						setLoading(false)
					})
			} catch (error) {
				console.warn(error)
				setLoading(false)
				setErr(true)
			}
		} else {
			setErr(true)
			setLoading(false)
		}
	}

	return (
		<>
			{sdd.distributionChannel !==
			OtpDistributionChannel.PushNotification ? (
				<div className="sdd-otp-section-sms-and-email">
					<div className="sdd-otp-information-top">
						<Trans t={t} i18nKey="SDDAuthOtpText">
							Code has been sent to
							<strong>
								{{
									state:
										sdd.distributionChannel ===
										OtpDistributionChannel.Email
											? maskedEmail(sdd.emailAddress)
											: maskedPhoneNr(sdd.phoneNumber),
								}}
							</strong>
							and is valid for another:
						</Trans>
					</div>
					<div
						className={`sdd-otp-timer ${
							timeOut ? 'time-out' : ''
						}`}>
						{time}
					</div>
					<>
						{timeOut ? (
							<></>
						) : (
							<>
								<div className="sdd-otp-information-bottom">
									<p>{t('SDDAuthOtpText2')}</p>
								</div>
								<div className="sdd-otp-code-entry-section">
									<input
										className="form-control"
										type="text"
										placeholder="Einmalpasswort"
										value={otpCode}
										onChange={(e) =>
											setOtpCode(e.target.value)
										}></input>
									<button
										disabled={
											otpCode === '' ||
											otpCode === undefined
										}
										type="button"
										className="btn btn-primary validate-btn"
										onClick={() => {
											setLoading(true)
											validateOtp(otpCode!)
										}}>
										{t('ProceedValidateAuthCode')}
									</button>
								</div>
							</>
						)}
					</>
					<div className="back-section">
						<button
							type="button"
							className="btn btn-secondary dark-gray back-btn"
							onClick={() => setAuthSelect(true)}>
							{timeOut ? t('Retry') : t('Back')}
						</button>
					</div>
				</div>
			) : (
				<div className="sdd-otp-section-mobileapp">
					<div className="sdd-otp-information">
						<p>{t('SDDAuthAppText')}</p>
					</div>
					<div className="sdd-otp-mobileapp-code-section">
						<span>{sdd.distributionChannel}</span>
					</div>
					<Trans t={t} i18nKey="SDDAuthAppText2">
						<div className="sdd-otp-information">
							<p>The code is valid for another:</p>
						</div>
						<div
							className={`sdd-otp-timer ${
								timeOut ? 'time-out' : ''
							}`}>
							{{ time: time }}
						</div>
						<div className="sdd-otp-information">
							<p>
								If you have not received any message within 5
								minutes, please go back one step to resend it or
								chose a different distribution method.
							</p>
						</div>
					</Trans>
					<div className="back-section">
						<button
							type="button"
							className="btn btn-outline dark-gray back-btn"
							onClick={() => setAuthSelect(true)}>
							{timeOut ? t('Retry') : t('Back')}
						</button>
					</div>
				</div>
			)}
		</>
	)
}
